<template>
  <section class="build-specification" aria-labelledby="specification-title">
    <header class="spec-heading">
      <div><span class="spec-eyebrow">THE COMPONENTS</span><h2 id="specification-title">Your build, in detail.</h2></div>
      <span class="spec-count">{{ items.length }} selected components</span>
    </header>
    <ol class="spec-list">
      <li v-for="(item, index) in items" :key="item.type" class="spec-row">
        <span class="spec-number">{{ String(index + 1).padStart(2, '0') }}</span>
        <div class="spec-artwork">
          <img v-if="source(item) && !unavailable[source(item)]" :src="source(item)" :alt="item.name" loading="lazy" @error="$set(unavailable, source(item), true)">
          <span v-else>Image unavailable</span>
        </div>
        <div class="spec-product">
          <span class="spec-category">{{ item.type }}</span>
          <h3>{{ item.name }}</h3>
          <div class="spec-finish"><Color :value="item.color" small /><span v-if="item.colorName">{{ item.colorName }}</span><span v-if="item.pair">Pair · 2 included</span></div>
        </div>
        <div class="spec-purchase">
          <span class="spec-price">{{ money(item.price) }}</span>
          <span v-if="item.pair" class="spec-unit">{{ money(item.unitPrice) }} each</span>
          <span v-if="item.weight && item.weight.grams" class="spec-unit">&asymp;{{ grams(item.weight) }}</span>
          <a v-if="item.url" :href="item.url" target="_blank" rel="noopener noreferrer" :aria-label="`Shop ${item.name}`">Shop component <span aria-hidden="true">↗</span></a>
        </div>
      </li>
    </ol>
    <footer class="spec-total"><span>Component total <small>Indicative retail price</small></span><strong>{{ money(total) }}</strong></footer>
    <footer v-if="weight" class="spec-total spec-weight"><span>Estimated weight <small>Claimed weights + {{ smallParts }} g small parts<template v-if="!weight.pedals">, without pedals</template></small></span><strong>&asymp;{{ kg(weight.grams) }} kg</strong></footer>
  </section>
</template>
<script>
import { Color } from '@/components';
import { kg, weightLabel, SMALL_PARTS } from '@/utils/weight';
export default {
  components: { Color },
  props: { items: { type: Array, default: () => [] }, weight: Object },
  data: () => ({ unavailable: {}, smallParts: SMALL_PARTS.grams }),
  computed: { total() { return this.items.reduce((sum, item) => sum + Number(item.price || 0), 0); } },
  methods: {
    kg,
    grams(w) { return w.count > 1 ? `2 × ${weightLabel(w.each)}` : weightLabel(w.grams); },
    source(item) { return item.image.front || item.image.src || item.image.thumb; },
    money(value) { return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(value || 0)); }
  }
};
</script>
<style scoped>
.build-specification { margin-top: 56px; text-align: left; color: #252c22; }
.spec-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; padding: 0 0 28px; }
.spec-eyebrow, .spec-category { display: block; font-size: 10px; letter-spacing: .13em; text-transform: uppercase; color: #697062; }
.spec-heading h2 { font-size: clamp(25px, 3vw, 38px); font-weight: 400; letter-spacing: -.035em; line-height: 1.2; margin: 9px 0 0; text-transform: none; }
.spec-count { font-size: 12px; color: #697062; white-space: nowrap; }
.spec-list { list-style: none; padding: 0 !important; margin: 0; border-top: 1px solid #cdd1c5; }
.spec-row { display: grid; grid-template-columns: 28px 132px minmax(0, 1fr) auto; gap: 24px; align-items: center; padding: 24px 0; border-bottom: 1px solid #dedfd7; break-inside: avoid; }
.spec-number { font-size: 11px; color: #858b80; align-self: start; padding-top: 6px; font-variant-numeric: tabular-nums; }
.spec-artwork { height: 112px; padding: 10px; background: #fff; display: flex; align-items: center; justify-content: center; }
.spec-artwork img { width: 100%; height: 100%; object-fit: contain; }
.spec-artwork > span { font-size: 11px; color: #697062; }
.spec-product h3 { margin: 7px 0 10px; font-size: 18px; line-height: 1.35; font-weight: 400; text-transform: none; overflow-wrap: anywhere; }
.spec-finish { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 11px; color: #697062; }
.spec-finish > * { margin: 0 !important; }
.spec-purchase { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.spec-price { font-size: 20px; letter-spacing: -.02em; font-variant-numeric: tabular-nums; white-space: nowrap; }
.spec-unit { font-size: 11px; color: #697062; }
.spec-purchase a { margin-top: 12px; font-size: 11px; color: #344b35; text-underline-offset: 4px; }
.spec-purchase a:focus-visible { outline: 2px solid #344b35; outline-offset: 5px; }
.spec-total { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 28px 0; font-size: 15px; }
.spec-total small { display: block; margin-top: 5px; color: #697062; font-size: 11px; }
.spec-total strong { font-size: 28px; font-weight: 400; letter-spacing: -.03em; }
.spec-weight { padding-top: 0; }
@media (max-width: 700px) {
  .build-specification { margin-top: 36px; }
  .spec-heading { align-items: flex-start; flex-direction: column; gap: 12px; }
  .spec-row { grid-template-columns: 76px minmax(0, 1fr); gap: 14px; padding: 20px 0; }
  .spec-number { display: none; }
  .spec-artwork { height: 80px; padding: 6px; }
  .spec-product h3 { font-size: 15px; }
  .spec-purchase { grid-column: 2; align-items: flex-start; gap: 4px; }
  .spec-price { font-size: 17px; }
  .spec-purchase a { margin-top: 5px; }
}
@media print { .spec-purchase a { display: none; } .spec-row { padding: 12px 0; } .spec-artwork { height: 80px; } }
</style>
